import { Controller } from '@hotwired/stimulus';

/**
 * agent-review-attach
 *
 * Panneau « Rattacher » de la revue de l'agent (spec revue-agent-cr § 4.4, D13) :
 * une photo non placée, les remarques du CR en cartes radio groupées par lot.
 *
 *   - « Rattacher » reste grisé tant qu'aucune remarque n'est choisie (`sync`,
 *     sur le `change` qui remonte des radios, y compris celui que
 *     `radio-card#select` émet) ;
 *   - la recherche masque les cartes dont le texte (`data-search-text`, posé
 *     SERVEUR : numéro et résumé) ne contient pas la saisie, puis les lots vidés,
 *     puis affiche le message « aucune remarque » si plus rien n'est visible.
 *
 * Le contrôleur n'écrit ni texte ni classe de couleur : il bascule `hidden` et
 * l'attribut `disabled`, rien d'autre.
 *
 * Targets:
 *   submit — le bouton « Rattacher »
 *   option — l'enveloppe d'une carte (porte `data-search-text`)
 *   group  — un lot (son titre et ses cartes)
 *   empty  — le message affiché quand la recherche ne trouve rien
 *
 * Actions:
 *   change->agent-review-attach#sync   (sur le formulaire)
 *   input->agent-review-attach#filter  (sur le champ de recherche)
 *
 * @example
 *   <div data-controller="agent-review-attach">
 *     <input type="search" data-action="input->agent-review-attach#filter">
 *     <form data-action="change->agent-review-attach#sync">
 *       <div data-agent-review-attach-target="group">
 *         <div data-agent-review-attach-target="option" data-search-text="obs-3 fissure">…radio…</div>
 *       </div>
 *       <p data-agent-review-attach-target="empty" class="hidden">Aucune remarque</p>
 *       <button type="submit" disabled data-agent-review-attach-target="submit">Rattacher</button>
 *     </form>
 *   </div>
 */
export const normalize = (text) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

export default class extends Controller {
  static targets = ['submit', 'option', 'group', 'empty'];

  connect() {
    this.sync();
  }

  sync() {
    if (!this.hasSubmitTarget) return;

    this.submitTarget.disabled = !this.element.querySelector('input[type="radio"]:checked');
  }

  filter(event) {
    const query = normalize(event.target.value);

    this.optionTargets.forEach((option) => {
      const match = query === '' || normalize(option.dataset.searchText).includes(query);
      option.classList.toggle('hidden', !match);
    });

    let anyVisible = false;
    this.groupTargets.forEach((group) => {
      const visible = this.optionTargets.some(
        (option) => group.contains(option) && !option.classList.contains('hidden')
      );
      group.classList.toggle('hidden', !visible);
      anyVisible ||= visible;
    });

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle('hidden', anyVisible);
  }
}
